import React, { useState, MouseEvent } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Backdrop,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/store';
import StatusBadge from '@/components/StatusBadge';
import { AdminGamic } from '../types';
import { Icons } from '@/icons';
import RemoteFile from '@/helper/RemoteFile';
import { MENU_MAP, MenuDescriptor } from './MenuMap';
import {
  deleteGamic,
  fetchAllGamicsAdmin,
  updateGamicStatus,
} from '@/store/Gamic';
import ApproveRejectDialog from '@/components/dialog/ApprovedDialog';
import { formatLocalDate } from '@/utils/FormateLocalDate';

interface Props {
  gamic: AdminGamic;
  setDialogOpen: (open: boolean) => void; 
  setDialogType: (type: 'removed' | 'approved' | 'rejected' | 'hold' | 'publish') => void;
}

const GamicRow: React.FC<Props> = ({ gamic, setDialogOpen, setDialogType }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  // const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [reasons, setReasons] = useState<string[]>([]);

  const menuOpen = Boolean(anchorEl);
  const dispatch = useDispatch<AppDispatch>();

  const openMenu = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const descriptors: MenuDescriptor[] =
    MENU_MAP[gamic.status] ?? MENU_MAP['Pending'];

  const close = () => {
    setIsApproveOpen(false);
    setIsRejectOpen(false);
    // setIsRemoveOpen(false);
    setReasons([]);
  };

  const handleApprove = async () => {
    try {
      await dispatch(
        updateGamicStatus({
          gamicId: gamic.id,
          status: 'approved',
          tags: reasons,
        }),
      );
      setDialogType('approved');
      setDialogOpen(true);
      dispatch(fetchAllGamicsAdmin({}));
      close();
    } catch (error) {
      console.error('Error updating gamic status:', error);
      close();
    }
  };

  const handleReject = async () => {
    try {
      await dispatch(
        updateGamicStatus({
          gamicId: gamic.id,
          status: 'rejected',
          tags: reasons,
        }),
      );
      setDialogType('rejected');
      setDialogOpen(true);
      dispatch(fetchAllGamicsAdmin({}));
      close();
    } catch (error) {
      console.error('Error updating gamic status:', error);
      close();
    }
  };

  const handleRemove = async () => {
    try {
      await dispatch(deleteGamic(gamic.id));
      setDialogType('removed');
      setDialogOpen(true);

      dispatch(fetchAllGamicsAdmin({}));
      close();
    } catch (error) {
      console.error('Error updating gamic status:', error);
      close();
    }
  };

  const handleHold = async () => {
    try {
      await dispatch(
        updateGamicStatus({
          gamicId: gamic.id,
          status: 'hold',
        }),
      );
      setDialogType('hold');
      setDialogOpen(true);
      dispatch(fetchAllGamicsAdmin({}));
    } catch (error) {
      console.error('Error updating gamic status:', error);
    }
  };

  const handlePublish = async () => {
    try {
      await dispatch(
        updateGamicStatus({
          gamicId: gamic.id,
          status: 'pending',
        }),
      );
      setDialogType('publish');
      setDialogOpen(true);
      dispatch(fetchAllGamicsAdmin({}));
    } catch (error) {
      console.error('Error updating gamic status:', error);
    }
  };

  return (
    <>
      <tr className='border-b'>
        <td className='p-2'>
          <RemoteFile
            className='h-[84px] w-[56px] object-cover rounded'
            keyPath={gamic.img}
            alt={gamic.title}
          />
        </td>
        <td className='p-4 whitespace-nowrap'>{formatLocalDate(gamic.timestamp)}</td>
        <td className='p-4'>{gamic.title}</td>
        <td className='p-4'>{gamic.category}</td>
        <td className='p-4'>{gamic.isDownloadLink}</td>
        <td className='p-4'>{gamic.consoleResult}</td>
        <td className='p-4'>
          <StatusBadge status={gamic.status} />
        </td>
        <td className='p-4 relative'>
          <IconButton size='small' onClick={openMenu}>
            <Icons.Menu />
          </IconButton>

          {menuOpen && (
            <Backdrop
              open
              onClick={closeMenu}
              sx={{
                position: 'fixed',
                zIndex: (theme) => theme.zIndex.modal - 1,
                bgcolor: 'rgba(0,0,0,0.3)',
              }}
            />
          )}

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: 291,
                borderRadius: 2,
                overflow: 'visible',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 10,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  boxShadow: '0px -5px 2px rgba(0,0,0,.1)',
                },
              },
            }}
          >
            {descriptors.map(({ key, icon, label, to, action }) => (
              <MenuItem
                sx={{ width: '100%', textAlign: 'left' }}
                key={key}
                {...(to
                  ? { component: NavLink, to, state: { gamic } }
                  : { component: 'button' })}
                onClick={() => {
                  closeMenu();
                  if (key === 'approve') setIsApproveOpen(true);
                  if (key === 'reject') setIsRejectOpen(true);
                  if (key === 'hold') handleHold();
                  if (key === 'remove') handleRemove();
                  if (key === 'publish') handlePublish();
                  action?.();
                }}
              >
                <ListItemIcon sx={{ height: '32px', width: '32px' }}>
                  {icon}
                </ListItemIcon>
                <ListItemText primary={label} />
              </MenuItem>
            ))}
          </Menu>
          <ApproveRejectDialog
            open={isApproveOpen}
            title='Approve Gamic'
            message='You are going to approve gamic'
            confirmLabel='Yes, Approve'
            cancelLabel='No, Cancel'
            onConfirm={handleApprove}
            onClose={close}
            setReasons={setReasons}
            reasons={reasons}
          />
          <ApproveRejectDialog
            open={isRejectOpen}
            title='Reject Gamic'
            message='You are going to reject gamic'
            confirmLabel='Yes, Reject'
            cancelLabel='No, Cancel'
            onConfirm={handleReject}
            onClose={close}
            setReasons={setReasons}
            reasons={reasons}
          />
        </td>
      </tr>
      
    </>
  );
};

export default GamicRow;
